/**
 * Clu's context meter: how full the prompt the model received this turn is,
 * against the size where older messages get summarized (50,000 tokens with
 * compaction) or the model's input window otherwise. The backend measures it
 * with Anthropic's token count, streams it as `event: context` and returns
 * the latest one as `context_meter` on the conversation.
 *
 * Spec: docs/plans/clu-context-meter-2026-09-25.md.
 */

export type ContextMeterRowKey =
  | 'system'
  | 'instructions'
  | 'tools'
  | 'guides'
  | 'conversation';

export type ContextMeter = {
  total: number;
  capacity: number;
  basis: 'compaction' | 'model_window';
  rows: Record<ContextMeterRowKey, number>;
};

/** The rows in the order the card lists them, with their colour square. */
export const CONTEXT_METER_ROWS: ReadonlyArray<{
  key: ContextMeterRowKey;
  label: string;
  swatch: string;
}> = [
  { key: 'system', label: 'System', swatch: 'bg-slate-500 dark:bg-slate-400' },
  {
    key: 'instructions',
    label: 'Instructions',
    swatch: 'bg-amber-500 dark:bg-amber-400',
  },
  { key: 'tools', label: 'Tools', swatch: 'bg-sky-500 dark:bg-sky-400' },
  {
    key: 'guides',
    label: 'Guides',
    swatch: 'bg-violet-500 dark:bg-violet-400',
  },
  {
    key: 'conversation',
    label: 'Conversation',
    swatch: 'bg-emerald-500 dark:bg-emerald-400',
  },
];

const count = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? Math.round(value)
    : null;

/**
 * A meter from the stream or the API, or null when the payload is not one.
 * The API's copy carries `model` and `measured_at` beside the event's fields;
 * those are dropped.
 */
export function parseContextMeter(raw: unknown): ContextMeter | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Record<string, unknown>;
  const total = count(value.total);
  const capacity = count(value.capacity);
  if (total === null || capacity === null || capacity === 0) return null;
  const rawRows =
    value.rows && typeof value.rows === 'object'
      ? (value.rows as Record<string, unknown>)
      : {};
  const rows = {} as Record<ContextMeterRowKey, number>;
  for (const { key } of CONTEXT_METER_ROWS) {
    rows[key] = count(rawRows[key]) ?? 0;
  }
  return {
    total,
    capacity,
    basis: value.basis === 'compaction' ? 'compaction' : 'model_window',
    rows,
  };
}

const rowFormat = new Intl.NumberFormat('en', {
  notation: 'compact',
  maximumFractionDigits: 1,
});
const headerFormat = new Intl.NumberFormat('en', {
  notation: 'compact',
  maximumFractionDigits: 0,
});

/** A row's count: 707, 2.7K, 16.4K. */
export const formatRowTokens = (tokens: number): string =>
  rowFormat.format(tokens);

/** The header's counts: 44K, 50K, 1M. */
export const formatHeaderTokens = (tokens: number): string =>
  headerFormat.format(tokens);

/**
 * How full, as a whole percent: at least 1 once anything is in the prompt,
 * at most 100 (a turn can start above the summary size and drop during it).
 */
export function contextPercent(total: number, capacity: number): number {
  if (capacity <= 0 || total <= 0) return 0;
  return Math.min(100, Math.max(1, Math.round((total / capacity) * 100)));
}

/**
 * The bar's segments: one per non-zero row in row order, each as a percent
 * of the bar. Measured against the capacity, or against the total when the
 * prompt is over it, so the segments never add up to more than the bar.
 */
export function contextSegments(
  meter: ContextMeter
): { key: ContextMeterRowKey; swatch: string; width: number }[] {
  const scale = Math.max(meter.capacity, meter.total);
  if (scale <= 0) return [];
  return CONTEXT_METER_ROWS.filter(({ key }) => meter.rows[key] > 0).map(
    ({ key, swatch }) => ({
      key,
      swatch,
      width: (meter.rows[key] / scale) * 100,
    })
  );
}
